import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React from 'react';

interface ConfirmModalProps {
    title: string;
    message: string;
    confirmLabel?: string;
    destructive?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

/** Generic confirmation dialog; `destructive` gives it the red delete-dialog look. */
const ConfirmModal: React.FC<ConfirmModalProps> = ({
    title,
    message,
    confirmLabel = 'Continue',
    destructive = false,
    onConfirm,
    onCancel
}) => (
    <div
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={onCancel}
    >
        <div
            className="bg-white rounded-xl shadow-lg w-full max-w-sm p-6 relative"
            onClick={(e) => e.stopPropagation()}
        >
            <button
                onClick={onCancel}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
            >
                <XMarkIcon className="w-5 h-5" />
            </button>

            <div className="flex items-start gap-4">
                {destructive && (
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                        <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                    <p className="text-sm text-gray-500 mt-2">{message}</p>
                </div>
            </div>

            <div className="flex justify-end items-center gap-3 mt-6">
                <button
                    onClick={onCancel}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={onConfirm}
                    className={`px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors ${
                        destructive
                            ? 'bg-red-600 hover:bg-red-700'
                            : 'bg-gray-800 hover:bg-gray-900'
                    }`}
                >
                    {confirmLabel}
                </button>
            </div>
        </div>
    </div>
);

export { ConfirmModal };
